import { useState, useEffect, useCallback } from "react"
import { auth } from "../components/Authenticator/firebase"
import { useNavigate } from "react-router-dom"
import { API_URL } from "../config/api"
import { 
  Users, 
  Shield, 
  Building2, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Check,
  X,
  Home,
  RefreshCw,
  AlertCircle,
  UserCheck,
  Database
} from "lucide-react"

export default function RolesManager() {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [areas, setAreas] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingAreas, setLoadingAreas] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [message, setMessage] = useState(null)
  const [mobileView, setMobileView] = useState('users')
  const [isMobile, setIsMobile] = useState(false)
  const [filterArea, setFilterArea] = useState('all')

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 900)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setLoadingAreas(true)
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        navigate("/login")
        return
      }

      const [usersRes, areasRes] = await Promise.all([
        fetch(`${API_URL}/api/roles/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/roles/areas`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ])

      if (usersRes.status === 403) {
        navigate("/home")
        return
      }

      const usersData = await usersRes.json()
      const areasData = await areasRes.json()

      if (usersData.success) setUsers(usersData.users || [])
      if (areasData.success) setAreas(areasData.areas || [])
    } catch (error) {
      console.error("Error:", error)
      setMessage({ type: 'error', text: 'Error cargando datos' })
    } finally {
      setLoading(false)
      setLoadingAreas(false)
    }
  }, [navigate])

  const assignArea = async (userId, areaId) => {
    try {
      setSaving(true)
      const token = await auth.currentUser?.getIdToken()
      
      const response = await fetch(`${API_URL}/api/roles/user/${userId}/assign`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ areaId })
      })

      const data = await response.json()
      
      if (data.success) {
        setMessage({ type: 'success', text: `Área asignada correctamente: ${data.areaName}` })
        // Actualizar usuario en la lista local
        setUsers(prev => prev.map(u => 
          u.uid === userId 
            ? { ...u, areaId, areaName: data.areaName }
            : u
        ))
        if (selectedUser?.uid === userId) {
          setSelectedUser(prev => ({ ...prev, areaId, areaName: data.areaName }))
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al asignar área' })
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' })
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(null), 3000)
    }
  }

  const removeRole = async (userId) => {
    try {
      setSaving(true)
      const token = await auth.currentUser?.getIdToken()
      
      const response = await fetch(`${API_URL}/api/roles/user/${userId}/role`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })

      const data = await response.json()
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Rol eliminado correctamente' })
        setUsers(prev => prev.map(u => 
          u.uid === userId 
            ? { ...u, areaId: null, areaName: 'Sin asignar' }
            : u
        ))
        if (selectedUser?.uid === userId) {
          setSelectedUser(prev => ({ ...prev, areaId: null, areaName: 'Sin asignar' }))
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' })
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(null), 3000)
    }
  }

  useEffect(() => {
    loadData()
  }, [loadData])

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase()
    const matchesSearch = (
      user.displayName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.uid?.toLowerCase().includes(search)
    )
    const matchesArea = filterArea === 'all' || 
      (filterArea === 'unassigned' && !user.areaId) ||
      user.areaId === filterArea
    return matchesSearch && matchesArea
  })

  const getInitials = (name) => {
    if (!name || name === "Sin nombre") return "?"
    return name.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2)
  }

  const getAvatarColor = (uid) => {
    const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#0ea5e9", "#3b82f6"]
    return colors[uid?.charCodeAt(0) % colors.length || 0]
  }

  const getAreaColor = (areaId) => {
    const colors = {
      'direccion': '#dc2626',
      'socios': '#ea580c',
      'rrhh': '#d97706',
      'administracion': '#65a30d',
      'contabilidad': '#16a34a',
      'facturacion': '#059669',
      'recepcion': '#0d9488',
      'informatica': '#0891b2',
      'produccion': '#0284c7',
      'produccion_encargos': '#2563eb',
      'mantenimiento': '#4f46e5',
      'calidad': '#7c3aed',
      'tecnico': '#9333ea',
      'riegos': '#c026d3',
      'sector_injertos': '#db2777',
      'sector_siembra': '#e11d48',
      'lavado_bandejas': '#be123c',
      'logistica': '#b91c1c',
      'cargas': '#c2410c',
      'sector_choferes': '#a16207',
      'garden': '#4d7c0f',
      'atencion_garden': '#15803d',
      'mantenimiento_jardines': '#047857'
    }
    return colors[areaId] || '#6b7280'
  }

  const panelVisible = (panel) => {
    if (!isMobile) return true
    return mobileView === panel
  }

  const goBack = () => {
    setMobileView('users')
    setSelectedUser(null)
  }

  const stats = {
    total: users.length,
    assigned: users.filter(u => u.areaId).length,
    unassigned: users.filter(u => !u.areaId).length
  }

  return (
    <div style={{
      display: "flex",
      height: "100vh",
      background: "#fbf9f4",
      fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
      overflow: "hidden"
    }}>
      {/* Mensaje de notificación */}
      {message && (
        <div style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          padding: "14px 20px",
          borderRadius: "12px",
          background: message.type === 'success' ? "#dcfce7" : "#fee2e2",
          color: message.type === 'success' ? "#166534" : "#dc2626",
          border: `1px solid ${message.type === 'success' ? "#bbf7d0" : "#fecaca"}`,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          animation: "slideIn 0.3s ease"
        }}>
          {message.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Panel 1: Lista de Usuarios */}
      {panelVisible('users') && (
        <div style={{
          width: isMobile ? "100%" : "400px",
          minWidth: isMobile ? "100%" : "400px",
          borderRight: isMobile ? "none" : "1px solid #e5e2dc",
          display: "flex",
          flexDirection: "column",
          background: "#fff"
        }}>
          {/* Header */}
          <div style={{ padding: "20px", borderBottom: "1px solid #f0ebe3" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "20px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Shield size={20} color="#fff" />
                </div>
                <div>
                  <h1 style={{ fontSize: "16px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
                    Gestión de Roles
                  </h1>
                  <p style={{ fontSize: "12px", color: "#6b7280", margin: 0 }}>
                    Asignar áreas a usuarios
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={loadData}
                  disabled={loading}
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <RefreshCw size={18} color="#6b7280" style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                </button>
                <button
                  onClick={() => navigate("/home")}
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Home size={18} color="#6b7280" />
                </button>
              </div>
            </div>

            {/* Stats */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "10px",
              marginBottom: "16px"
            }}>
              <div style={{
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                padding: "14px 12px",
                borderRadius: "12px",
                textAlign: "center",
                border: "1px solid #bfdbfe"
              }}>
                <Users size={18} color="#2563eb" style={{ marginBottom: "4px" }} />
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e40af" }}>{stats.total}</div>
                <div style={{ fontSize: "10px", color: "#3b82f6", fontWeight: "500" }}>Total</div>
              </div>
              <div style={{
                background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                padding: "14px 12px",
                borderRadius: "12px",
                textAlign: "center",
                border: "1px solid #bbf7d0"
              }}>
                <UserCheck size={18} color="#16a34a" style={{ marginBottom: "4px" }} />
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#15803d" }}>{stats.assigned}</div>
                <div style={{ fontSize: "10px", color: "#22c55e", fontWeight: "500" }}>Asignados</div>
              </div>
              <div style={{
                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                padding: "14px 12px",
                borderRadius: "12px",
                textAlign: "center",
                border: "1px solid #fcd34d"
              }}>
                <AlertCircle size={18} color="#d97706" style={{ marginBottom: "4px" }} />
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#b45309" }}>{stats.unassigned}</div>
                <div style={{ fontSize: "10px", color: "#f59e0b", fontWeight: "500" }}>Sin Rol</div>
              </div>
            </div>

            {/* Filtro por área */}
            <select
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                fontSize: "13px",
                background: "#f9fafb",
                marginBottom: "12px",
                cursor: "pointer"
              }}
            >
              <option value="all">Todas las áreas</option>
              <option value="unassigned">Sin asignar</option>
              {areas.map(area => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>

            {/* Search */}
            <div style={{ position: "relative" }}>
              <Search size={16} color="#9ca3af" style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)"
              }} />
              <input
                type="text"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "11px 12px 11px 38px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  fontSize: "13px",
                  boxSizing: "border-box",
                  background: "#f9fafb",
                  outline: "none"
                }}
              />
            </div>
          </div>

          {/* Lista de usuarios */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
                <div style={{
                  width: "32px",
                  height: "32px",
                  border: "3px solid #e5e7eb",
                  borderTopColor: "#7c3aed",
                  borderRadius: "50%",
                  margin: "0 auto 12px",
                  animation: "spin 1s linear infinite"
                }} />
                Cargando usuarios...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ padding: "60px 20px", textAlign: "center", color: "#9ca3af" }}>
                <Users size={32} style={{ marginBottom: "12px", opacity: 0.4 }} />
                <div>No hay usuarios</div>
              </div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.uid}
                  onClick={() => {
                    setSelectedUser(user)
                    if (isMobile) setMobileView('details')
                  }}
                  style={{
                    padding: "14px 20px",
                    borderBottom: "1px solid #f3f4f6",
                    cursor: "pointer",
                    background: selectedUser?.uid === user.uid ? "#f5f3ff" : "transparent",
                    transition: "all 0.15s",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}
                >
                  <div style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: user.photoURL ? `url(${user.photoURL}) center/cover` : getAvatarColor(user.uid),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "15px",
                    fontWeight: "600",
                    flexShrink: 0
                  }}>
                    {!user.photoURL && getInitials(user.displayName)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1f2937",
                      marginBottom: "3px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}>
                      {user.displayName || "Sin nombre"}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginBottom: "4px"
                    }}>
                      {user.email}
                    </div>
                    <div style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      background: user.areaId ? `${getAreaColor(user.areaId)}15` : "#f3f4f6",
                      color: user.areaId ? getAreaColor(user.areaId) : "#9ca3af",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "11px",
                      fontWeight: "500",
                      border: `1px solid ${user.areaId ? `${getAreaColor(user.areaId)}30` : "#e5e7eb"}`
                    }}>
                      <Building2 size={12} />
                      {user.areaName || "Sin asignar"}
                    </div>
                  </div>

                  <ChevronRight size={18} color="#d1d5db" />
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Panel 2: Detalles y Asignación */}
      {panelVisible('details') && (
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          background: "#fbf9f4",
          width: isMobile ? "100%" : "auto"
        }}>
          {!selectedUser ? (
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: "#9ca3af",
              padding: "40px"
            }}>
              <Shield size={56} style={{ marginBottom: "16px", opacity: 0.2 }} />
              <div style={{ textAlign: "center", fontSize: "14px", color: "#6b7280" }}>
                Selecciona un usuario para asignar área
              </div>
            </div>
          ) : (
            <>
              {/* Header del usuario */}
              <div style={{
                padding: "24px",
                background: "#fff",
                borderBottom: "1px solid #e5e2dc"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  {isMobile && (
                    <button
                      onClick={goBack}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex"
                      }}
                    >
                      <ChevronLeft size={24} color="#6b7280" />
                    </button>
                  )}
                  <div style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "16px",
                    background: selectedUser.photoURL ? `url(${selectedUser.photoURL}) center/cover` : getAvatarColor(selectedUser.uid),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "22px",
                    fontWeight: "600",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                  }}>
                    {!selectedUser.photoURL && getInitials(selectedUser.displayName)}
                  </div>
                  <div>
                    <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
                      {selectedUser.displayName || "Sin nombre"}
                    </h2>
                    <p style={{ fontSize: "14px", color: "#6b7280", margin: "4px 0" }}>
                      {selectedUser.email}
                    </p>
                    <p style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "monospace" }}>
                      ID: {selectedUser.uid}
                    </p>
                  </div>
                </div>

                {/* Área actual */}
                <div style={{
                  marginTop: "20px",
                  padding: "16px",
                  background: selectedUser.areaId ? `${getAreaColor(selectedUser.areaId)}10` : "#f9fafb",
                  borderRadius: "12px",
                  border: `1px solid ${selectedUser.areaId ? `${getAreaColor(selectedUser.areaId)}30` : "#e5e7eb"}`
                }}>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>
                    Área actual:
                  </div>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px"
                    }}>
                      <div style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "10px",
                        background: selectedUser.areaId ? getAreaColor(selectedUser.areaId) : "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <Building2 size={18} color="#fff" />
                      </div>
                      <span style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: selectedUser.areaId ? getAreaColor(selectedUser.areaId) : "#6b7280"
                      }}>
                        {selectedUser.areaName || "Sin asignar"}
                      </span>
                    </div>
                    {selectedUser.areaId && (
                      <button
                        onClick={() => removeRole(selectedUser.uid)}
                        disabled={saving}
                        style={{
                          background: "#fee2e2",
                          color: "#dc2626",
                          border: "1px solid #fecaca",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px"
                        }}
                      >
                        <X size={14} />
                        Quitar rol
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Lista de áreas */}
              <div style={{
                flex: 1,
                overflowY: "auto",
                padding: "24px"
              }}>
                <h3 style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}>
                  <Database size={16} />
                  Asignar área ({areas.length} disponibles)
                </h3>

                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "12px"
                }}>
                  {loadingAreas ? (
                    <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "40px", color: "#9ca3af" }}>
                      Cargando áreas...
                    </div>
                  ) : (
                    areas.map(area => (
                      <button
                        key={area.id}
                        onClick={() => assignArea(selectedUser.uid, area.id)}
                        disabled={saving || selectedUser.areaId === area.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "14px 16px",
                          background: selectedUser.areaId === area.id ? `${getAreaColor(area.id)}15` : "#fff",
                          border: `2px solid ${selectedUser.areaId === area.id ? getAreaColor(area.id) : "#e5e7eb"}`,
                          borderRadius: "12px",
                          cursor: selectedUser.areaId === area.id ? "default" : "pointer",
                          transition: "all 0.2s",
                          textAlign: "left",
                          opacity: saving ? 0.6 : 1
                        }}
                      >
                        <div style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "10px",
                          background: `${getAreaColor(area.id)}20`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0
                        }}>
                          <Building2 size={20} color={getAreaColor(area.id)} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#1f2937",
                            marginBottom: "2px"
                          }}>
                            {area.name}
                          </div>
                          <div style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            textTransform: "capitalize"
                          }}>
                            Nivel: {area.accessLevel}
                          </div>
                        </div>
                        {selectedUser.areaId === area.id && (
                          <Check size={18} color={getAreaColor(area.id)} />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
